import { FormEvent, useEffect, useState } from "react";
import { supabase } from "./lib/supabase";
import "./sales-management.css";
import { SaleFinancialPanel } from "./SaleFinancialPanel";
import { VehicleMediaPanel } from "./VehicleMediaPanel";
import { type SaleOpportunitySeed } from "./StandCrm";
import { CurrencyInput } from "./CurrencyInput";

type Vehicle = {
  id: string;
  stock_number: string;
  make: string;
  model: string;
  version: string | null;
  registration: string | null;
  first_registration_year: number | null;
  mileage_km: number;
  fuel_type: string | null;
  transmission: string | null;
  color: string | null;
  stock_status: string;
  publication_status: string;
  sale_price_cents: number | null;
};
type Sale = {
  id: string;
  organization_id: string;
  vehicle_id: string;
  customer_name: string;
  customer_tax_id: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  sale_price_cents: number;
  currency_code: string;
  status: string;
  financial_status: string;
  invoice_status: string;
  sold_publication_mode: string;
  seller_user_id: string;
  sale_date: string;
  notes: string | null;
  opportunity_id: string | null;
  customer_id: string | null;
  created_at: string;
};
type TradeIn = {
  id: string;
  sale_id: string;
  stock_vehicle_id: string;
  owner_name: string;
  owner_tax_id: string | null;
  registration: string | null;
  vin: string | null;
  make: string;
  model: string;
  version: string | null;
  first_registration_year: number | null;
  mileage_km: number;
  condition_notes: string | null;
  agreed_value_cents: number;
  currency_code: string;
};
const money = (cents: number, currency: string) =>
  new Intl.NumberFormat(currency === "BRL" ? "pt-BR" : "pt-PT", {
    style: "currency",
    currency,
  }).format(cents / 100);
const saleStatusLabel = (status: string) =>
  status === "sold"
    ? "Vendida"
    : status === "cancelled"
      ? "Cancelada"
      : "Em processo";

export function SalesManagement({
  organizationId,
  currencyCode,
  writable,
  canValidate,
  saleOpportunity,
  onSaleOpportunityConsumed,
}: {
  organizationId: string;
  currencyCode: string;
  writable: boolean;
  canValidate: boolean;
  saleOpportunity: SaleOpportunitySeed | null;
  onSaleOpportunityConsumed: () => void;
}) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]),
    [sales, setSales] = useState<Sale[]>([]),
    [tradeIns, setTradeIns] = useState<TradeIn[]>([]),
    [selected, setSelected] = useState<Sale | null>(null),
    [creating, setCreating] = useState(false),
    [hasTradeIn, setHasTradeIn] = useState(false),
    [saving, setSaving] = useState(false),
    [message, setMessage] = useState<string | null>(null);
  const load = async () => {
    const [vr, sr, tr] = await Promise.all([
      supabase
        .from("vehicles")
        .select(
          "id,stock_number,make,model,version,registration,first_registration_year,mileage_km,fuel_type,transmission,color,stock_status,publication_status,sale_price_cents",
        )
        .eq("organization_id", organizationId)
        .eq("creation_status", "active")
        .order("stock_number"),
      supabase
        .from("vehicle_sales")
        .select(
          "id,organization_id,vehicle_id,customer_name,customer_tax_id,customer_email,customer_phone,sale_price_cents,currency_code,status,financial_status,invoice_status,sold_publication_mode,seller_user_id,sale_date,notes,opportunity_id,customer_id,created_at",
        )
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false }),
      supabase
        .from("vehicle_trade_ins")
        .select(
          "id,sale_id,stock_vehicle_id,owner_name,owner_tax_id,registration,vin,make,model,version,first_registration_year,mileage_km,condition_notes,agreed_value_cents,currency_code",
        )
        .eq("organization_id", organizationId),
    ]);
    if (vr.error || sr.error || tr.error)
      setMessage(
        vr.error?.message ??
          sr.error?.message ??
          tr.error?.message ??
          "Não foi possível carregar as vendas.",
      );
    const nextSales = (sr.data ?? []) as Sale[];
    setVehicles((vr.data ?? []) as Vehicle[]);
    setSales(nextSales);
    setTradeIns((tr.data ?? []) as TradeIn[]);
    setSelected((current) =>
      current
        ? (nextSales.find((item) => item.id === current.id) ?? null)
        : null,
    );
  };
  useEffect(() => {
    void load();
  }, [organizationId]);
  useEffect(() => {
    if (saleOpportunity) setCreating(true);
  }, [saleOpportunity]);
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 10000);
    return () => clearTimeout(timer);
  }, [message]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setSaving(true);
    const salePayload = {
      p_vehicle_id: f.get("vehicle_id"),
      p_customer_name: f.get("customer_name"),
      p_customer_tax_id: f.get("customer_tax_id"),
      p_customer_email: f.get("customer_email"),
      p_customer_phone: f.get("customer_phone"),
      p_sale_price_cents: Math.round(Number(f.get("sale_price")) * 100),
      p_sale_date: f.get("sale_date"),
      p_sold_publication_mode: f.get("sold_publication_mode"),
      p_notes: f.get("notes"),
      p_has_trade_in: hasTradeIn,
      p_trade_owner_name: f.get("trade_owner_name"),
      p_trade_owner_tax_id: f.get("trade_owner_tax_id"),
      p_trade_registration: f.get("trade_registration"),
      p_trade_vin: f.get("trade_vin"),
      p_trade_make: f.get("trade_make"),
      p_trade_model: f.get("trade_model"),
      p_trade_version: f.get("trade_version"),
      p_trade_year: f.get("trade_year") ? Number(f.get("trade_year")) : null,
      p_trade_mileage: Number(f.get("trade_mileage") || 0),
      p_trade_condition: f.get("trade_condition"),
      p_trade_value_cents: f.get("trade_value")
        ? Math.round(Number(f.get("trade_value")) * 100)
        : null,
    };
    const { data, error } = saleOpportunity
      ? await supabase.rpc("register_opportunity_sale", {
          p_opportunity_id: saleOpportunity.opportunityId,
          p_customer_tax_id: salePayload.p_customer_tax_id,
          p_sale_price_cents: salePayload.p_sale_price_cents,
          p_sale_date: salePayload.p_sale_date,
          p_sold_publication_mode: salePayload.p_sold_publication_mode,
          p_notes: salePayload.p_notes,
          p_has_trade_in: salePayload.p_has_trade_in,
          p_trade_owner_name: salePayload.p_trade_owner_name,
          p_trade_owner_tax_id: salePayload.p_trade_owner_tax_id,
          p_trade_registration: salePayload.p_trade_registration,
          p_trade_vin: salePayload.p_trade_vin,
          p_trade_make: salePayload.p_trade_make,
          p_trade_model: salePayload.p_trade_model,
          p_trade_version: salePayload.p_trade_version,
          p_trade_year: salePayload.p_trade_year,
          p_trade_mileage: salePayload.p_trade_mileage,
          p_trade_condition: salePayload.p_trade_condition,
          p_trade_value_cents: salePayload.p_trade_value_cents,
        })
      : await supabase.rpc("register_vehicle_sale", salePayload);
    setSaving(false);
    if (error) {
      setMessage(error.message);
      return;
    }
    const result = data as { trade_in_stock_number?: string | null };
    setMessage(
      result?.trade_in_stock_number
        ? `Venda registada. A retoma ${result.trade_in_stock_number} entrou no stock como rascunho não publicado.`
        : saleOpportunity
          ? "Venda registada. A oportunidade foi convertida e o prospeto passou a cliente. Complete 100% da composição do pagamento para finalizar a venda."
          : "Venda registada. Complete 100% da composição do pagamento para a finalizar.",
    );
    setCreating(false);
    if (saleOpportunity) onSaleOpportunityConsumed();
    setHasTradeIn(false);
    await load();
  };
  const eligible = vehicles.filter((v) =>
    ["in_stock", "reserved"].includes(v.stock_status),
  );
  return (
    <section className="sales-management">
      <header>
        <div>
          <p className="eyebrow">Processos comerciais</p>
          <h2>Vendas</h2>
          <p>
            Registe o cliente, a venda e uma eventual retoma num único processo.
          </p>
        </div>
        {writable && (
          <button className="stock-primary" onClick={() => setCreating(true)}>
            + Registar venda
          </button>
        )}
      </header>
      {message && (
        <button className="stock-notice" role="status" aria-live="polite" onClick={() => setMessage(null)}>
          {message} ×
        </button>
      )}
      <div className="sales-summary">
        <article>
          <small>Processos</small>
          <strong>{sales.length}</strong>
        </article>
        <article>
          <small>Em curso</small>
          <strong>
            {sales.filter((s) => s.status === "in_progress").length}
          </strong>
        </article>
        <article>
          <small>Concluídas</small>
          <strong>{sales.filter((s) => s.status === "sold").length}</strong>
        </article>
      </div>
      <div className="sales-list">
        {sales.length === 0 ? (
          <div className="stock-empty">
            <strong>Ainda não existem vendas.</strong>
            <span>
              Registe a primeira venda quando uma negociação for formalizada.
            </span>
          </div>
        ) : (
          sales.map((s) => {
            const v = vehicles.find((x) => x.id === s.vehicle_id),
              t = tradeIns.find((x) => x.sale_id === s.id);
            return (
              <button
                type="button"
                className="sale-row"
                key={s.id}
                onClick={() => setSelected(s)}
              >
                <div>
                  <strong>
                    {v?.registration || v?.stock_number || "Sem identificação"}
                  </strong>
                  <span>
                    {v
                      ? `${v.make} ${v.model}${v.version ? ` · ${v.version}` : ""}`
                      : "Viatura não identificada"}
                  </span>
                </div>
                <div>
                  <strong>{s.customer_name}</strong>
                  <span>
                    {t
                      ? `Com retoma: ${t.registration || `${t.make} ${t.model}`}`
                      : "Sem retoma"}
                  </span>
                </div>
                <div>
                  <strong>{money(s.sale_price_cents, s.currency_code)}</strong>
                  <span>
                    {new Date(s.sale_date).toLocaleDateString("pt-PT")}
                  </span>
                </div>
                <em>{saleStatusLabel(s.status)}</em>
              </button>
            );
          })
        )}
      </div>
      {creating && (
        <SaleForm
          vehicles={eligible}
          currencyCode={currencyCode}
          hasTradeIn={hasTradeIn}
          setHasTradeIn={setHasTradeIn}
          saving={saving}
          opportunity={saleOpportunity}
          submit={submit}
          close={() => {setCreating(false);if(saleOpportunity)onSaleOpportunityConsumed()}}
        />
      )}{" "}
      {selected && (
        <SaleDetailDialog
          organizationId={organizationId}
          writable={writable}
          canValidate={canValidate}
          sale={selected}
          vehicle={vehicles.find((v) => v.id === selected.vehicle_id) ?? null}
          tradeIn={tradeIns.find((t) => t.sale_id === selected.id) ?? null}
          tradeVehicle={
            vehicles.find(
              (v) =>
                v.id ===
                tradeIns.find((t) => t.sale_id === selected.id)
                  ?.stock_vehicle_id,
            ) ?? null
          }
          onChanged={load}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}

function SaleForm({
  vehicles,
  currencyCode,
  hasTradeIn,
  setHasTradeIn,
  saving,
  opportunity,
  submit,
  close,
}: {
  vehicles: Vehicle[];
  currencyCode: string;
  hasTradeIn: boolean;
  setHasTradeIn: (v: boolean) => void;
  saving: boolean;
  opportunity: SaleOpportunitySeed | null;
  submit: (e: FormEvent<HTMLFormElement>) => void;
  close: () => void;
}) {
  return (
    <div className="stock-dialog-backdrop">
      <section className="stock-dialog sale-dialog">
        <header>
          <div>
            <p className="eyebrow">Novo processo</p>
            <h2>Registar venda</h2>
            <p>
              A retoma será criada automaticamente no stock como uma ficha por
              completar.
            </p>
          </div>
          <button type="button" className="stock-close" onClick={close}>
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <fieldset className="stock-section">
            <legend>Venda e cliente</legend>
            <div className="stock-form-grid">
              <label className="wide">
                <span>Viatura vendida *</span>
                <select name="vehicle_id" required defaultValue={opportunity?.vehicleId??""} disabled={Boolean(opportunity)}>
                  <option value="">Selecione a viatura</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.registration || v.stock_number} · {v.make} {v.model}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Cliente *</span>
                <input name="customer_name" required defaultValue={opportunity?.customerName??""} readOnly={Boolean(opportunity)} />
              </label>
              <label>
                <span>NIF</span>
                <input name="customer_tax_id" />
              </label>
              <label>
                <span>Email</span>
                <input name="customer_email" type="email" defaultValue={opportunity?.customerEmail??""} readOnly={Boolean(opportunity)} />
              </label>
              <label>
                <span>Telefone</span>
                <input name="customer_phone" defaultValue={opportunity?.customerPhone??""} readOnly={Boolean(opportunity)} />
              </label>
              <label>
                <span>Valor da venda ({currencyCode}) *</span>
                <CurrencyInput
                  currencyCode={currencyCode}
                  name="sale_price"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                />
              </label>
              <label>
                <span>Data da venda *</span>
                <input
                  name="sale_date"
                  type="date"
                  defaultValue={new Date().toISOString().slice(0, 10)}
                  required
                />
              </label>
              <label className="wide">
                <span>Publicação depois de concluir a venda</span>
                <select name="sold_publication_mode" defaultValue="unpublish">
                  <option value="unpublish">Retirar do site</option>
                  <option value="keep_published">
                    Manter publicada com etiqueta “Vendida”
                  </option>
                </select>
                <small>
                  Aplicada somente quando o processo passar para Vendida.
                </small>
              </label>
              <label className="wide">
                <span>Notas</span>
                <textarea name="notes" rows={2} />
              </label>
            </div>
          </fieldset>
          <fieldset className="stock-section trade-in-section">
            <legend>Retoma</legend>
            <label className="trade-toggle">
              <input
                type="checkbox"
                checked={hasTradeIn}
                onChange={(e) => setHasTradeIn(e.target.checked)}
              />
              <span>
                <strong>Existe retoma nesta venda</strong>
                <small>
                  A viatura entrará no stock como rascunho e nunca será
                  publicada automaticamente.
                </small>
              </span>
            </label>
            {hasTradeIn && (
              <div className="stock-form-grid trade-grid">
                <label>
                  <span>Proprietário *</span>
                  <input name="trade_owner_name" required />
                </label>
                <label>
                  <span>NIF do proprietário</span>
                  <input name="trade_owner_tax_id" />
                </label>
                <label>
                  <span>Matrícula</span>
                  <input name="trade_registration" />
                </label>
                <label>
                  <span>VIN</span>
                  <input name="trade_vin" />
                </label>
                <label>
                  <span>Marca *</span>
                  <input name="trade_make" required />
                </label>
                <label>
                  <span>Modelo *</span>
                  <input name="trade_model" required />
                </label>
                <label>
                  <span>Versão</span>
                  <input name="trade_version" />
                </label>
                <label>
                  <span>Ano</span>
                  <input
                    name="trade_year"
                    type="number"
                    min="1900"
                    max="2100"
                  />
                </label>
                <label>
                  <span>Quilometragem</span>
                  <input name="trade_mileage" type="number" min="0" />
                </label>
                <label>
                  <span>Valor acordado ({currencyCode}) *</span>
                  <CurrencyInput
                    currencyCode={currencyCode}
                    name="trade_value"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                  />
                </label>
                <label className="wide">
                  <span>Estado e observações</span>
                  <textarea name="trade_condition" rows={2} />
                </label>
              </div>
            )}
          </fieldset>
          <footer>
            <button type="button" className="stock-secondary" onClick={close}>
              Cancelar
            </button>
            <button className="stock-primary" disabled={saving}>
              {saving ? "A guardar…" : "Guardar"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function SaleDetailDialog({
  sale,
  vehicle,
  tradeIn,
  tradeVehicle,
  organizationId,
  writable,
  canValidate,
  onChanged,
  onClose,
}: {
  sale: Sale;
  vehicle: Vehicle | null;
  tradeIn: TradeIn | null;
  tradeVehicle: Vehicle | null;
  organizationId: string;
  writable: boolean;
  canValidate: boolean;
  onChanged: () => Promise<void>;
  onClose: () => void;
}) {
  const [editingTrade, setEditingTrade] = useState(false);
  const detail = (label: string, value: string | number | null | undefined) => (
    <div>
      <small>{label}</small>
      <strong>
        {value === null || value === undefined || value === ""
          ? "Não indicado"
          : value}
      </strong>
    </div>
  );
  return (
    <div className="stock-dialog-backdrop">
      <section className="stock-dialog sale-detail">
        <header>
          <div>
            <p className="eyebrow">Ficha da venda</p>
            <h2>{vehicle?.registration || vehicle?.stock_number || "Venda"}</h2>
            <p>
              {vehicle
                ? `${vehicle.make} ${vehicle.model}${vehicle.version ? ` · ${vehicle.version}` : ""}`
                : "Viatura não identificada"}
            </p>
          </div>
          <button type="button" className="stock-close" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="sale-detail-body">
          <section>
            <h3>Estado do processo</h3>
            <div className="sale-detail-grid">
              {detail(
                "Estado",
                sale.status === "sold" ? "Vendida" : "Em processo",
              )}
              {detail(
                "Data",
                new Date(sale.sale_date).toLocaleDateString("pt-PT"),
              )}
              {detail(
                "Valor da venda",
                money(sale.sale_price_cents, sale.currency_code),
              )}
              {detail(
                "Depois de vendida",
                sale.sold_publication_mode === "keep_published"
                  ? "Publicada com etiqueta Vendida"
                  : "Retirar do site",
              )}
            </div>
          </section>
          <section>
            <h3>Cliente</h3>
            <div className="sale-detail-grid">
              {detail("Nome", sale.customer_name)}
              {detail("NIF", sale.customer_tax_id)}
              {detail("Telefone", sale.customer_phone)}
              {detail("Email", sale.customer_email)}
            </div>
            {sale.notes && <p className="sale-notes">{sale.notes}</p>}
          </section>
          <section>
            <h3>Viatura vendida</h3>
            <div className="sale-detail-grid">
              {detail("Matrícula", vehicle?.registration)}
              {!vehicle?.registration &&
                detail("Código interno", vehicle?.stock_number)}
              {detail(
                "Marca e modelo",
                vehicle ? `${vehicle.make} ${vehicle.model}` : null,
              )}
              {detail("Versão", vehicle?.version)}
              {detail("Ano", vehicle?.first_registration_year)}
              {detail(
                "Quilometragem",
                vehicle
                  ? `${vehicle.mileage_km.toLocaleString("pt-PT")} km`
                  : null,
              )}
              {detail("Combustível", vehicle?.fuel_type)}
              {detail("Transmissão", vehicle?.transmission)}
            </div>
          </section>
          <section className="trade-detail">
            <h3>Retoma</h3>
            {tradeIn ? (
              <>
                <div className="trade-publication-warning">
                  <strong>Não publicada</strong>
                  <span>
                    A retoma entrou no stock como rascunho e nunca é publicada
                    automaticamente.
                  </span>
                </div>
                <button
                  type="button"
                  className="stock-secondary trade-profile-button"
                  onClick={() => setEditingTrade(true)}
                >
                  Abrir e completar ficha da retoma
                </button>
                <div className="sale-detail-grid">
                  {detail("Matrícula", tradeIn.registration)}
                  {detail("VIN", tradeIn.vin)}
                  {detail("Marca e modelo", `${tradeIn.make} ${tradeIn.model}`)}
                  {detail("Versão", tradeIn.version)}
                  {detail("Ano", tradeIn.first_registration_year)}
                  {detail(
                    "Quilometragem",
                    `${tradeIn.mileage_km.toLocaleString("pt-PT")} km`,
                  )}
                  {detail(
                    "Valor da retoma",
                    money(tradeIn.agreed_value_cents, tradeIn.currency_code),
                  )}
                  {detail("Proprietário", tradeIn.owner_name)}
                  {detail("NIF do proprietário", tradeIn.owner_tax_id)}
                  {detail(
                    "Ficha criada no stock",
                    tradeVehicle?.registration || tradeVehicle?.stock_number,
                  )}
                  {detail(
                    "Publicação",
                    tradeVehicle?.publication_status === "published"
                      ? "Publicada"
                      : "Rascunho — não publicada",
                  )}
                </div>
                {tradeIn.condition_notes && (
                  <p className="sale-notes">{tradeIn.condition_notes}</p>
                )}
              </>
            ) : (
              <p className="sale-empty-detail">Esta venda não possui retoma.</p>
            )}
          </section>
          <SaleFinancialPanel
            sale={sale}
            organizationId={organizationId}
            tradeIn={tradeIn}
            writable={writable}
            canValidate={canValidate}
            onChanged={onChanged}
          />
        </div>
      </section>
      {editingTrade && tradeIn && (
        <TradeInEditor
          tradeIn={tradeIn}
          organizationId={organizationId}
          writable={writable}
          onClose={() => setEditingTrade(false)}
          onSaved={async () => {
            await onChanged();
            setEditingTrade(false);
          }}
        />
      )}
    </div>
  );
}

function TradeInEditor({
  tradeIn,
  organizationId,
  writable,
  onClose,
  onSaved,
}: {
  tradeIn: TradeIn;
  organizationId: string;
  writable: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setSaving(true);
    const { error: requestError } = await supabase.rpc(
      "update_trade_in_profile",
      {
        p_trade_in_id: tradeIn.id,
        p_registration: data.get("registration"),
        p_vin: data.get("vin"),
        p_make: data.get("make"),
        p_model: data.get("model"),
        p_version: data.get("version"),
        p_year: data.get("year") ? Number(data.get("year")) : null,
        p_mileage: Number(data.get("mileage") || 0),
        p_condition: data.get("condition"),
      },
    );
    setSaving(false);
    if (requestError) {
      setError(requestError.message);
      return;
    }
    await onSaved();
  };
  return (
    <div className="stock-dialog-backdrop nested-dialog">
      <section className="stock-dialog trade-edit-dialog">
        <header>
          <div>
            <p className="eyebrow">Ficha da retoma</p>
            <h2>
              {tradeIn.registration || `${tradeIn.make} ${tradeIn.model}`}
            </h2>
            <p>A venda permanece aberta por baixo desta ficha.</p>
          </div>
          <button type="button" className="stock-close" onClick={onClose}>
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <div className="stock-form-grid">
            <label>
              <span>Matrícula</span>
              <input
                name="registration"
                defaultValue={tradeIn.registration ?? ""}
              />
            </label>
            <label>
              <span>VIN</span>
              <input name="vin" defaultValue={tradeIn.vin ?? ""} />
            </label>
            <label>
              <span>Marca *</span>
              <input name="make" defaultValue={tradeIn.make} required />
            </label>
            <label>
              <span>Modelo *</span>
              <input name="model" defaultValue={tradeIn.model} required />
            </label>
            <label>
              <span>Versão</span>
              <input name="version" defaultValue={tradeIn.version ?? ""} />
            </label>
            <label>
              <span>Ano</span>
              <input
                name="year"
                type="number"
                min="1900"
                max="2100"
                defaultValue={tradeIn.first_registration_year ?? ""}
              />
            </label>
            <label>
              <span>Quilometragem</span>
              <input
                name="mileage"
                type="number"
                min="0"
                defaultValue={tradeIn.mileage_km}
              />
            </label>
            <label className="wide">
              <span>Estado e observações</span>
              <textarea
                name="condition"
                defaultValue={tradeIn.condition_notes ?? ""}
              />
            </label>
          </div>
          {error && <p className="form-error">{error}</p>}
          <VehicleMediaPanel
            organizationId={organizationId}
            vehicleId={tradeIn.stock_vehicle_id}
            writable={writable}
          />
          <footer>
            <button type="button" className="stock-secondary" onClick={onClose}>
              Fechar
            </button>
            <button className="stock-primary" disabled={saving}>
              {saving ? "A guardar…" : "Guardar"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
